import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { MenuItem } from 'src/app/core/models/main-menu.models';

/** One static route the palette can open, as `Area -> Section -> Item`. */
export interface PaletteEntry {
  area: string;
  section: string;
  item: string;
  link: string | Array<string | number>;
  icon?: IconProp;
  /** Lower-cased, diacritics-stripped copies for matching. */
  haystack: { area: string; section: string; item: string };
}

export interface PaletteMatch extends PaletteEntry {
  /** 0 item prefix, 1 item substring, 2 section / area substring, 3 full path substring. */
  rank: number;
}

export function normalizeForSearch(text: string): string {
  return (text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Flattens one area's (already access-filtered) menu tree: a header item names
 * the section of everything below it, a leaf with a link becomes an entry, a
 * parent without a link only lends its children the current section.
 */
export function flattenMenuArea(
  area: string,
  items: MenuItem[],
): PaletteEntry[] {
  const out: PaletteEntry[] = [];
  const walk = (list: MenuItem[], section: string): void => {
    for (const item of list) {
      const nextSection = item.isHeader ? item.title : section;
      if (item.link && !item.isHeader) {
        out.push({
          area,
          section,
          item: item.title,
          link: item.link,
          icon: item.icon,
          haystack: {
            area: normalizeForSearch(area),
            section: normalizeForSearch(section),
            item: normalizeForSearch(item.title),
          },
        });
      }
      if (item.children?.length) walk(item.children, nextSection);
    }
  };
  walk(items, '');
  return out;
}

/**
 * Substring match on item, then section / area, then the whole path; the
 * result keeps the menu order inside each rank. An empty query lists every
 * entry so the palette doubles as a sitemap.
 */
export function matchPaletteEntries(
  entries: PaletteEntry[],
  query: string,
  limit: number,
): PaletteMatch[] {
  const q = normalizeForSearch(query);
  if (!q) return entries.map((e) => ({ ...e, rank: 0 }));
  const matches: PaletteMatch[] = [];
  for (const e of entries) {
    const { area, section, item } = e.haystack;
    let rank: number | null = null;
    if (item.startsWith(q)) rank = 0;
    else if (item.includes(q)) rank = 1;
    else if (section.includes(q) || area.includes(q)) rank = 2;
    else if (`${area} ${section} ${item}`.includes(q)) rank = 3;
    if (rank !== null) matches.push({ ...e, rank });
  }
  return matches.sort((a, b) => a.rank - b.rank).slice(0, limit);
}
